import React, { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Stack, Switch } from '@mui/material';
import { FileCsv, Plus, X, TrashSimple } from '@phosphor-icons/react';
import { Formik } from 'formik';
import { Oval } from 'react-loader-spinner';
import { api } from '../../services/api';
import { TextComponent } from '../TextComponent';
import { InputComponent } from '../InputComponent';
import { DragAndDrop } from '../DragAndDrop';
import { Button } from '../Button';
import { UserDataContext } from '../../providers/userData';
import { LoadingContext } from '../../providers/loading';
import { OpenContext } from '../../providers/openModal';
import { useTheme } from '../../providers/theme';
import { Field, FieldOption } from '../../types/FieldType';
import { FIELD_TYPE } from '../../FieldTypes';
import { FieldOptionsModalSchema } from './FieldOptionsModalSchema';
import {
  BoxFieldOptionsModal, ContentModal, Footer, HeaderModal, InputContainer, InputOptionsContainer, ModalContainer,
  SwitchContainer
} from './styles';
import './scrollbarStyled.css';
import { useTranslation } from 'react-i18next';
import { DialogAlert } from '../DialogAlert';

interface FieldOptionsDataModalType {
  dataField: Field;
  fieldTypeValue: string;
  isEditFieldModalOpen: boolean;
  getProjectData: () => Promise<void>;
  fieldsLength: number;
}

export function FieldOptionsDataModal({
  dataField, fieldTypeValue, isEditFieldModalOpen, getProjectData, fieldsLength
}: FieldOptionsDataModalType) {
  const { projectId } = useParams();
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const { userData } = useContext(UserDataContext);
  const { isOpen, setIsOpen } = useContext(OpenContext);
  const { theme } = useTheme();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [fieldOptionIdToDelete, setFieldOptionIdToDelete] = useState<string>('');
  const [fieldOptionIdToDeleteTitle, setFieldOptionIdToDeleteTitle] = useState<string>('');
  const [handleCsvUpload, setHandleCsvUpload] = useState<boolean>(false);
  const [valuesFieldOptions, setValuesFieldOptions] = useState('');
  const [arrayFieldOptions, setArrayFieldOptions] = useState<FieldOption[]>([]);
  const [csvOptions, setCsvOptions] = useState<FieldOption[]>([]);
  const [arrayFieldOptionsEditable, setArrayFieldOptionsEditable] = useState<FieldOption[]>([]);
  const [newOptions, setNewOptions] = useState<FieldOption[]>([]);
  const [fieldOptionsWasEdited, setFieldOptionsWasEdited] = useState<boolean>(false);
  const [wasInsertedAFieldOption, setWasInsertedAFieldOption] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen.values.options) {
      setArrayFieldOptionsEditable(dataField?.field_options);
    }
  }, [isOpen.values.options]);

  function insertInputValueInArray(inputValue: string) {
    if (inputValue.trim().length > 0) {
      const newObject = { value: inputValue, order: arrayFieldOptions.length };

      setArrayFieldOptions([...arrayFieldOptions, newObject]);
      setValuesFieldOptions('');

      return;
    }

    alert(t('options-field-empty'));
  }

  function insertInputValueInArrayInEditableMode(inputValue: string, fieldId: string) {
    if (inputValue.trim().length > 0) {
      setFieldOptionsWasEdited(true);
      setWasInsertedAFieldOption(true);
      const newObject = {
        value: inputValue,
        field_id: `${fieldId}`,
        order: arrayFieldOptionsEditable ? arrayFieldOptionsEditable.length : 0,
      };

      !arrayFieldOptionsEditable ?
        setArrayFieldOptionsEditable([newObject]) :
        setArrayFieldOptionsEditable([...arrayFieldOptionsEditable, newObject]);

      setNewOptions([...newOptions, newObject]);
      setValuesFieldOptions('');
      return;
    }

    alert(t('options-field-empty'));
  }

  function handleInputChange(index: number, event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setFieldOptionsWasEdited(true);
    const newInputValues = [...arrayFieldOptionsEditable];
    newInputValues[index].value = event.target.value;
    setArrayFieldOptionsEditable(newInputValues);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isSameObject(uneditedField: any, editedField: any) {
    if (typeof uneditedField !== 'object' || typeof editedField !== 'object') {
      return false;
    }

    const keysA = Object.keys(uneditedField);
    const keysB = Object.keys(editedField);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if (typeof uneditedField[key] === 'object' && typeof editedField[key] === 'object') {
        if (!isSameObject(uneditedField[key], editedField[key])) {
          return false;
        }
      } else if (uneditedField[key] !== editedField[key]) {
        return false;
      }
    }

    return true;
  }

  async function createFieldData(values: { title: string; description: string; isRequired: boolean }) {
    if (csvOptions.length === 0 && arrayFieldOptions.length === 0) {
      alert(t('options-field-requisition'));
    } else {
      setIsLoading({ value: true });
      setIsOpen((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          options: false
        }
      }));
      await api.post(`/project/${projectId}/field`, {
        'fields': [
          {
            title: values.title,
            description: values.description,
            owner_id: userData.userEmail,
            order: fieldsLength,
            is_active: true,
            is_file: false,
            is_required: values.isRequired,
            field_type_id: fieldTypeValue === 'checkbox' ? FIELD_TYPE.checkbox : fieldTypeValue === 'dropdown' ? FIELD_TYPE.dropdown : fieldTypeValue === 'radio' ? FIELD_TYPE.radio : fieldTypeValue,
            project_id: projectId,
            field_options: handleCsvUpload ? csvOptions : arrayFieldOptions
          }
        ]
      })
        .then(async () => {
          setValuesFieldOptions('');
          setArrayFieldOptions([]);
          setArrayFieldOptionsEditable([]);
          setHandleCsvUpload(false);
          await getProjectData();
          setIsLoading({ value: false });
        })
        .catch(async (error) => {
          console.log('ERROR: ', error);
          // alert(t('field-csv-error'));
          await getProjectData();
          setIsLoading({ value: false });
        });
    }
  }

  async function updateFieldData(fieldId: string, fieldTypeId: string, values: { title: string; description: string; isRequired: boolean | undefined }) {
    const { ...fieldData } = dataField;

    const newDataFieldObject = {
      fieldData,
    };

    const { fieldData: newFieldData, } = newDataFieldObject;

    const editedFormField = {
      title: values.title,
      description: values.description,
      owner_id: userData.userEmail,
      id: fieldId,
      is_active: true,
      is_file: false,
      is_required: values.isRequired,
      field_type_id: fieldTypeId,
      project_id: projectId,
    };

    if (!isSameObject(newFieldData, editedFormField)) {
      setIsLoading({ value: true });
      setIsOpen((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          options: false
        }
      }));
      await api.put(`/project/${projectId}/field?id=${fieldId}`, editedFormField)
        .then(async () => {
          await getProjectData();
          setIsLoading({ value: false });
        })
        .catch(error => console.log(error));
    }

    if (fieldOptionsWasEdited) {
      setIsLoading({ value: true });
      setIsOpen((prevState) => ({
        ...prevState,
        values: {
          ...prevState.values,
          options: false
        }
      }));
      if (wasInsertedAFieldOption) {
        await api.post(`/fields/${fieldId}/field_options`, {
          options: newOptions
        })
          .then(async () => {
            setNewOptions([]);
            await getProjectData();
            setIsLoading({ value: false });
          })
          .catch(error => console.log(error));
      }

    } else {
      setFieldOptionsWasEdited(false);
      return;
    }
  }

  function handleClose() {
    setDialogOpen(false);
  }

  function handleDeleteFieldOptionDialogOpen(fieldOptionId?: string, fieldOptionTitle?: string) {
    setFieldOptionIdToDeleteTitle(fieldOptionTitle || '');
    setFieldOptionIdToDelete(fieldOptionId || '');
    setDialogOpen(true);
  }

  async function handleDeleteFieldOption() {
    setIsLoading({ value: true });
    setDialogOpen(false);

    const result = arrayFieldOptionsEditable.filter(item => item.id !== fieldOptionIdToDelete);
    setArrayFieldOptionsEditable(result);

    await api.delete(`/field_option?id=${fieldOptionIdToDelete}`)
      .then(async () => {
        await getProjectData();
        setIsLoading({ value: false });
      })
      .catch(error => console.log(error));
  }

  function handleRemoveItemInOptionsArray(value: string) {
    const result = arrayFieldOptionsEditable.filter(item => item.value !== value);
    setArrayFieldOptionsEditable(result);
  }

  function handleCsv() {
    setHandleCsvUpload(prevState => !prevState);
    setArrayFieldOptions([]);
  }

  function handleChangeCsvOptions(data: string) {
    const stringArrayCsv = data.split(/\r?\n/).filter(Boolean);
    if(stringArrayCsv.length > 50) {
      alert(t('csv-limit-field-options'));
      setHandleCsvUpload(prevState => !prevState);
    }
    const csvData = stringArrayCsv.map((value, index) => ({ value, order: index }));
    setCsvOptions(csvData);
  }

  function toggle() {
    setIsOpen((prevState) => ({
      ...prevState,
      values: {
        ...prevState.values,
        options: false
      }
    }));
  }

  return (
    <>
      {isOpen.values.options && !isEditFieldModalOpen && (
        <ModalContainer>
          <BoxFieldOptionsModal className='scrollStyled'>
            <HeaderModal>
              <span style={{ flexGrow: 1, textAlign: 'center' }}>{`NOVO ${fieldTypeValue.toUpperCase()}`}</span>
              <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
            </HeaderModal>

            <Formik
              initialValues={{
                title: '',
                description: '',
                owner_id: userData.userEmail || '',
                isRequired: false
              }}
              validationSchema={FieldOptionsModalSchema}
              validateOnChange={true}
              validateOnMount={true}
              onSubmit={values => createFieldData(values)}
            >
              {({ errors, values, touched, handleSubmit, handleChange }) => (
                <form onSubmit={handleSubmit}>
                  <ContentModal>
                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-name')}
                      </TextComponent>
                      <InputComponent
                        id='title'
                        type='text'
                        value={values.title}
                        onChange={handleChange}
                        placeholder={t('title-placeholder')}
                      />
                      {errors.title && touched.title ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.title}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-description')}
                      </TextComponent>
                      <InputComponent
                        id='description'
                        type='text'
                        value={values.description}
                        onChange={handleChange}
                        placeholder={t('write-text-placeholder')}
                      />
                      {errors.description && touched.description ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.description}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-name')}
                      </TextComponent>
                      <InputComponent
                        id='fieldOwnerId'
                        type='text'
                        value={userData.userEmail}
                        readOnly
                      />
                    </InputContainer>

                    <TextComponent
                      fontWeight='bold'
                      fontSize='14px'
                    >
                      {t('button-project-field-options')}
                    </TextComponent>

                    <InputOptionsContainer>
                      <InputComponent
                        id='fieldOptions'
                        type='text'
                        value={valuesFieldOptions}
                        onChange={(e) => setValuesFieldOptions(e.target.value)}
                        placeholder={t('insert-value-placeholder')}
                      />
                      <Button
                        id='csvBtn'
                        type='button'
                        children={<FileCsv size={28} color='#FFF' />}
                        onClick={() => handleCsv()}
                        color={theme.colors.main}
                        sxhover={theme.colors.hoverMain}
                        sxwidth='50px'
                      />

                      <Button
                        id='addValues'
                        type='button'
                        children={<Plus size={28} color='#FFF' />}
                        onClick={() => insertInputValueInArray(valuesFieldOptions)}
                        color={theme.colors.green}
                        sxhover={theme.colors.hoverGreen}
                        sxwidth='50px'
                      />
                    </InputOptionsContainer>

                    {handleCsvUpload && (
                      <DragAndDrop
                        handleChangeCsvOptions={handleChangeCsvOptions}
                      />
                    )}

                    {arrayFieldOptions?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item) => (
                      <React.Fragment key={item.value}>
                        <InputContainer>
                          <InputComponent
                            id='fieldOptionsArrayItem'
                            type='text'
                            value={item.value}
                            readOnly
                          />
                        </InputContainer>
                      </React.Fragment>
                    ))}

                    <Footer>
                      <SwitchContainer>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch id='isRequired' name='isRequired' value={values.isRequired} onChange={handleChange} color='warning' />
                          <span>{t('obligatory-field')}</span>
                        </Stack>
                      </SwitchContainer>

                      <Button
                        id='save'
                        type='submit'
                        color={theme.colors.main}
                        sxhover={theme.colors.hoverMain}
                        sxmargintop='20px'
                      >
                        {isLoading.value ? (
                          <Oval color='#fff' secondaryColor='#fff' height='20px' />
                        ) : t('group-page-button')}
                      </Button>
                    </Footer>
                  </ContentModal>
                </form>
              )}
            </Formik>
          </BoxFieldOptionsModal>
        </ModalContainer>
      )}

      {isOpen.values.options && isEditFieldModalOpen && (
        <ModalContainer>
          <BoxFieldOptionsModal className='scrollStyled'>
            <HeaderModal>
              <span style={{ flexGrow: 1, textAlign: 'center' }}>{isEditFieldModalOpen === true ? dataField?.title : `NOVO ${fieldTypeValue.toUpperCase()}`}</span>
              <X onClick={() => toggle()} size={25} color={theme.colors.red} weight='bold' cursor='pointer' />
            </HeaderModal>

            <Formik
              initialValues={{
                title: dataField?.title,
                description: dataField?.description,
                owner_id: userData.userEmail || '',
                isRequired: dataField?.is_required === null ? false : dataField?.is_required
              }}
              validationSchema={FieldOptionsModalSchema}
              validateOnChange={true}
              validateOnMount={true}
              onSubmit={values => updateFieldData(dataField?.id, dataField?.field_type_id, values)}
            >
              {({ values, errors, touched, handleSubmit, handleChange }) => (
                <form onSubmit={handleSubmit}>
                  <ContentModal>
                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-name')}
                      </TextComponent>
                      <InputComponent
                        id='title'
                        type='text'
                        value={values.title}
                        onChange={handleChange}
                        placeholder={t('title-placeholder')}
                      />
                      {errors.title && touched.title ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.title}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-description')}
                      </TextComponent>
                      <InputComponent
                        id='description'
                        type='text'
                        value={values.description}
                        onChange={handleChange}
                        placeholder={t('write-text-placeholder')}
                      />
                      {errors.description && touched.description ? (
                        <div>
                          <span style={{ fontSize: '12px' }}>{errors.description}</span>
                        </div>
                      ) : null}
                    </InputContainer>

                    <InputContainer>
                      <TextComponent
                        fontWeight='bold'
                        fontSize='14px'
                      >
                        {t('button-project-field-manager')}
                      </TextComponent>
                      <InputComponent
                        id='fieldOwnerId'
                        type='text'
                        value={userData.userEmail}
                        readOnly
                      />
                    </InputContainer>

                    <TextComponent
                      fontWeight='bold'
                      fontSize='14px'
                    >
                      {t('button-project-field-options')}
                    </TextComponent>
                    <InputOptionsContainer>
                      <InputComponent
                        id='fieldOptions'
                        type='text'
                        value={valuesFieldOptions}
                        onChange={(e) => setValuesFieldOptions(e.target.value)}
                        placeholder={t('insert-value-placeholder')}
                      />
                      <Button
                        id='addValuesEditableMode'
                        type='button'
                        children={<Plus size={28} color='#FFF' />}
                        onClick={() => insertInputValueInArrayInEditableMode(valuesFieldOptions, dataField?.id)}
                        color={theme.colors.green}
                        sxhover={theme.colors.hoverGreen}
                        sxwidth='50px'
                      />
                    </InputOptionsContainer>

                    {arrayFieldOptionsEditable?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((item, index) => (
                      <React.Fragment key={index}>
                        <InputOptionsContainer>
                          <InputComponent
                            key={index}
                            id={item.id}
                            type='text'
                            onChange={(event) => handleInputChange(index, event)}
                            placeholder={item.value}
                          />
                          {item.id ?
                            <TrashSimple onClick={() => handleDeleteFieldOptionDialogOpen(item?.id, item.value)} size={28} color={theme.colors.red} cursor='pointer' />
                            : <TrashSimple onClick={() => handleRemoveItemInOptionsArray(item?.value)} size={28} color={theme.colors.red} weight='fill' cursor='pointer' />
                          }
                        </InputOptionsContainer>
                      </React.Fragment>
                    ))}

                    <Footer>
                      <SwitchContainer>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Switch
                            id='isRequired'
                            name='isRequired'
                            value={values.isRequired}
                            onChange={handleChange}
                            checked={values.isRequired ? true : false}
                            color='warning'
                          />
                          <span>{t('obligatory-field')}</span>
                        </Stack>
                      </SwitchContainer>

                      <Button
                        id='saveEditable'
                        type='submit'
                        color={theme.colors.main}
                        sxhover={theme.colors.hoverMain}
                        sxmargintop='20px'
                      >
                        {isLoading.value ? (
                          <Oval color='#fff' secondaryColor='#fff' height='20px' />
                        ) : 'ATUALIZAR'}
                      </Button>
                    </Footer>
                  </ContentModal>
                </form>
              )}
            </Formik>
          </BoxFieldOptionsModal>
        </ModalContainer>
      )}

      <DialogAlert
        handleDelete={handleDeleteFieldOption}
        dialogOpen={dialogOpen}
        handleClose={handleClose}
        dialogTitle={t('delete-option-field-option')}
        dialogMessage={t('delete-verification-field-option', {fieldOptionIdToDeleteTitle})}
      />
    </>
  );
}
